<template>
  <div class="asc-page-container">
    <asc-page-wrapper
      v-bind="{ ...$props, ...$attrs }"
      :feature-flags="featureFlags"
      :global-filter-active="isGlobalFilterActive"
      :global-filter-enabled="isGlobalFilterEnabled"
      :global-filter-disabled="isGlobalFilterDisabled"
      :global-filter-text="getGlobalFilterText"
      :global-filter-tooltip="getGlobalFilterTooltip"
      :global-filter-visible="isGlobalFilterVisible"
      :on-close-alert="handleCloseAlert"
      :on-log-out="handleLogOut"
      :on-manage-gaf="handleManageGaf"
      :profile="profile"
      :connectivity-issues="connectivityIssues"
      :is-airline-user="isAirlineUser"
      :router="getUpdatedRouter"
      :user-roles="roles"
      :user-permissions="permissions"
      :user-airlines="airlines"
    >
      <slot></slot>
    </asc-page-wrapper>

    <asc-global-filter-dialog
      :data="globalFilter"
      :show-dialog="globalFilterDialogVisible"
      :airlines-with-master-models="getMappedAirlines"
      :master-models-with-airlines="getMappedMasterModels"
      :on-close="handleCloseGlobalFilterDialog"
      :on-accept="handleGlobalFilterDialog"
    ></asc-global-filter-dialog>

    <asc-global-filter-confirmation-dialog
      :show-dialog="globalFilterConfirmationDialogVisible"
      :on-close="handleCloseConfirmationDialog"
      :on-cancel="handleCancelConfirmationDialog"
      :on-accept="handleGlobalFilterConfirmationDialog"
    ></asc-global-filter-confirmation-dialog>

    <asc-logout-dialog
      :show-dialog="logoutConfirmDialogVisible"
      :on-accept="handleLogoutAccept"
      :on-close="handleLogoutDialogClose"
    ></asc-logout-dialog>
  </div>
</template>

<script>
import { AscPageContainerMixin } from '@/components/mixins'
import AscPageWrapper from '@/components/organisms/AscPageWrapper'
import AscGlobalFilterDialog from '@/components/molecules/AscGlobalFilterDialog'
import AscGlobalFilterConfirmationDialog from '@/components/molecules/AscGlobalFilterConfirmationDialog'
import AscLogoutDialog from '@/components/molecules/AscLogoutDialog'
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import {
  ACMF_REPORT_LIST_CONSTS as ARLC,
  USER_CONSTS as UC,
  LOOKUP_CONSTS as LC,
  GLOBAL_FILTER_CONSTS as GF,
  CONNECTIVITY_CONSTS as CC,
  FEATURE_FLAG_CONSTS as FF,
} from '@/stores/consts'
import { PERMISSION_LEVEL_CONSTS, RESOURCES } from '@/utilities/appConsts'
import { updateRouterRoutesGivenPermissions } from '@/utilities/routing'
import AppFeatureFlags from '@/utilities/appFeatureFlags'

export default {
  name: 'AscPageContainer',

  components: {
    AscPageWrapper,
    AscGlobalFilterDialog,
    AscGlobalFilterConfirmationDialog,
    AscLogoutDialog,
  },

  mixins: [AscPageContainerMixin],

  props: {
    footerText: {
      type: String,
      required: true,
    },
    currentYear: {
      type: Number,
      required: true,
    },
    apiPageFailureCount: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      router: this.$router,
      logoutConfirmDialogVisible: false,
      permissionLevelConsts: PERMISSION_LEVEL_CONSTS,
      resources: RESOURCES,
    }
  },

  computed: {
    ...mapState(UC.NAMESPACE, ['profile', 'roles', 'airlines', 'permissions']),
    ...mapState(GF.NAMESPACE, ['globalFilter', 'globalFilterConfirmation']),
    ...mapState(CC.NAMESPACE, ['connectivityIssues']),
    ...mapState(FF.NAMESPACE, ['featureFlags']),
    ...mapGetters(LC.NAMESPACE, ['mappedUserMasterModelsWithAirlines']),
    ...mapGetters(UC.NAMESPACE, [
      'hasRole',
      'isAcmfUser',
      'isAirlineUser',
      'mappedUserAirlinesWithMasterModels',
    ]),
    ...mapGetters(ARLC.NAMESPACE, [
      'mappedAcmfAirlinesWithMasterModels',
      'mappedAcmfMasterModelsWithAirlines',
    ]),
    isAcmfOnlyUser() {
      return this.isAcmfUser && !this.isAirlineUser
    },

    getMappedAirlines() {
      if (this.isAcmfOnlyUser) {
        return this.mappedAcmfAirlinesWithMasterModels.filter(
          i => i.items && i.items.length > 0,
        )
      }
      return this.mappedUserAirlinesWithMasterModels.filter(
        i => i.items && i.items.length > 0,
      )
    },

    getMappedMasterModels() {
      if (this.isAcmfOnlyUser) {
        return this.mappedAcmfMasterModelsWithAirlines
          .filter(i => i.items && i.items.length > 0)
          .map(mmwa => {
            return {
              ...mmwa,
              items: mmwa.items.filter(
                item =>
                  item.masterModelsWithPHMAgreements &&
                  item.masterModelsWithPHMAgreements.length > 0,
              ),
            }
          })
      }
      return this.mappedUserMasterModelsWithAirlines
        .filter(i => i.items && i.items.length > 0)
        .map(mmwa => {
          return {
            ...mmwa,
            items: mmwa.items.filter(
              item =>
                item.masterModelsWithPHMAgreements &&
                item.masterModelsWithPHMAgreements.length > 0,
            ),
          }
        })
    },

    getUpdatedRouter() {
      const router = this.$router

      router.options.maps = updateRouterRoutesGivenPermissions(
        this.$router,
        this.hasRole,
        this.profile.phmAirlines,
        this.hasResourcePermission({
          resource: this.resources.MAINTENANCE_REC,
          permissionLevel: this.permissionLevelConsts.READ,
        }),
        this.hasResourcePermission({
          resource: this.resources.AOG_FEET_MANAGEMENT,
          permissionLevel: this.permissionLevelConsts.READ,
        }),
      )

      // Update dynamic breadcrumbs values as meta are not dynamic
      router.afterEach(to => {
        if (to.meta && to.meta.breadcrumbs && to.meta.breadcrumbs.length) {
          const dynamicMetaIndex = to.meta.breadcrumbs.findIndex(
            item => item.dynamic && item.referenceParam,
          )

          if (dynamicMetaIndex > -1) {
            const referenceParam =
              to.meta.breadcrumbs[dynamicMetaIndex].referenceParam

            if (to.params[referenceParam]) {
              const dynamicObj = {
                dynamic: true,
                referenceParam: referenceParam,
                text: to.params[referenceParam],
              }
              to.meta.breadcrumbs.splice(dynamicMetaIndex, 1, dynamicObj)
            }
          }
        }
      })

      return router
    },

    isGlobalFilterVisible() {
      if (this.isAcmfOnlyUser) {
        return this.mappedAcmfAirlinesWithMasterModels.length > 0
      }
      return this.mappedUserAirlinesWithMasterModels.length > 0
    },

    isConnectivityActive() {
      return AppFeatureFlags.getConnectivityBanner()
    },
  },

  watch: {
    // Watch all three properties deeply
    hasRole: {
      handler() {
        this.initRouter()
      },
      deep: true,
    },
    isAcmfUser: {
      handler() {
        this.initRouter()
      },
      deep: true,
    },
    isAirlineUser: {
      handler() {
        this.initRouter()
      },
      deep: true,
    },
  },

  created() {
    this.initGlobalFilter()
    this.initLookups()
    if (this.isConnectivityActive) this.initConnectivityNotification()
  },

  mounted() {
    this.handleFeedback()
  },

  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions(ARLC.NAMESPACE, [
      ARLC.GET_ACMF_AIRLINES,
      ARLC.GET_ACMF_MASTER_MODELS,
    ]),
    ...mapActions(LC.NAMESPACE, [LC.GET_MASTER_MODELS]),
    ...mapActions(GF.NAMESPACE, [
      GF.GET_GLOBAL_FILTER,
      GF.GET_GLOBAL_FILTER_CONFIRMATION,
    ]),
    ...mapActions(CC.NAMESPACE, [CC.GET_FULL_CONNECTIVITY_ISSUES]),
    ...mapMutations(GF.NAMESPACE, [
      GF.UPDATE_GLOBAL_FILTER,
      GF.UPDATE_GLOBAL_FILTER_CONFIRMATION,
    ]),
    ...mapMutations(['updateApiCallStatus']),

    async initGlobalFilter() {
      await Promise.all([
        this[GF.GET_GLOBAL_FILTER](),
        this[GF.GET_GLOBAL_FILTER_CONFIRMATION](),
      ])
      this.tempGlobalFilterConfirmation = this.globalFilterConfirmation
    },

    async initLookups() {
      if (this.isAcmfOnlyUser) {
        await Promise.all([
          this[ARLC.GET_ACMF_AIRLINES](),
          this[ARLC.GET_ACMF_MASTER_MODELS](),
        ])
      } else {
        await Promise.all([this[LC.GET_MASTER_MODELS]()])
      }
    },

    async initConnectivityNotification() {
      if (this.isAirlineUser && this.isConnectivityActive)
        await this[CC.GET_FULL_CONNECTIVITY_ISSUES]()
    },
    initRouter() {
      this.$router.options.routes = updateRouterRoutesGivenPermissions(
        this.$router,
        this.hasRole,
        this.profile.airlines,
        this.hasResourcePermission({
          resource: this.resources.MAINTENANCE_REC,
          permissionLevel: this.permissionLevelConsts.READ,
        }),
        this.hasResourcePermission({
          resource: this.resources.AOG_FLEET_MANAGEMENT,
          permissionLevel: this.permissionLevelConsts.READ,
        }),
      )
    },

    applyGlobalFilter() {
      this[GF.UPDATE_GLOBAL_FILTER](this.tempGlobalFilter)
    },

    applyGlobalFilterConfirmation() {
      this[GF.UPDATE_GLOBAL_FILTER_CONFIRMATION](
        this.tempGlobalFilterConfirmation,
      )
    },

    handleCloseAlert() {
      this.updateApiCallStatus('reset')
    },

    handleLogOut() {
      this.logoutConfirmDialogVisible = true
    },

    async handleLogoutAccept() {
      this.handleResetConnectivity()
      await this.$auth.tokenManager.remove('accessToken')
      await this.$auth.signOut()
    },

    handleLogoutDialogClose() {
      this.logoutConfirmDialogVisible = false
    },

    handleResetConnectivity() {
      if (this.isConnectivityActive)
        sessionStorage.removeItem('connectivity_check')
    },
    handleFeedback() {
      const containers = document.getElementsByClassName('QSISlider')
      const footerHeight =
        document.getElementsByClassName('v-footer')[0].offsetHeight || 42
      // Loop through each QSISlider
      for (let i = 0; i < containers.length; i++) {
        const QSISlider = containers[i]
        // Get an array of all the <div> elements inside the QSISlider
        const divElements = QSISlider.getElementsByTagName('div')
        // Apply different styles to each <div> element based on their index
        for (let j = 0; j < divElements.length; j++) {
          const div = divElements[j]
          if (j === 0) {
            div.style.top = `-${footerHeight}px`
            div.style.left = '191px' // Style for Div 1
          } else if (j === 1) {
            div.style.top = `-${footerHeight}px`
            div.style.left = '191px' // Style for Div 2
          } else if (j === 3) {
            div.style.top = `-${footerHeight - 10}px`
            div.style.left = '230px' // Style for Div 3
          }
        }
      }
    },
  },
}
</script>
